import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import React, { useState, useEffect, useCallback } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { FormGroup, Label } from "reactstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ReactComponent as LocationIcon } from "../../../assets/utils/images/svgs/maps-icon.svg";
import { useCalculateDistance, useSaveAddress, useValidateAddress } from "Api/Common/CommonComponents";


const AddressVerificationModal = ({
    apiAfterConfirm,
    isOpen,
    toggle,
    addressData,
    validatedAddressData,
    setIsValidating,
    setIsSaving,
    setIsCalculating,
    isValidating,
    setModalMessageType,
    isValidated,
    addressConfirmed,
    isSaving,
    isCalculating,
    modalMessage,
    setModalMessage,
    modalMessageType,
    setValidationMessageType,
    validationMessage,
    validationMessageType,
    setIsValidated,
    setValidationMessage,
    setValidationError,
    validationError,
    formattedAddress,
    setAddressConfirmed,
    formDataState,
    apiAftercalculation,
    setFormattedAddress,
}) => {
    const [apiError, setApiError] = useState("");
    const [showCalculateButton, setShowCalculateButton] = useState(false);

    const { trigger: validateAddressTrigger } = useValidateAddress();
    const { trigger: saveAddressTrigger } = useSaveAddress();
    const { trigger: calculateDistanceTrigger } = useCalculateDistance();

    const validationSchema = Yup.object().shape({
        tAddress: Yup.string()
            .max(500, "Adres mag maximaal 500 tekens bevatten")
            .trim()
            .required("Adres is verplicht"),
        vPostCode: Yup.string()
            .max(10, "Postcode mag maximaal 10 tekens bevatten")
            .trim()
            .required("Postcode is verplicht"),
        vCity: Yup.string()
            .max(30, "Plaats mag maximaal 30 tekens bevatten")
            .trim()
            .required("Plaats is verplicht"),
        vCountry: Yup.string()
            .trim()
            .required("Land is verplicht"),
    });

    const cities = [
        { key: "", value: "--- Kies land ---" },
        { key: "NL", value: "Nederland" },
        { key: "BE", value: "België" },
        { key: "DE", value: "Duitsland" },
    ];

    useEffect(() => {
        if (validatedAddressData?.verified === 1) {
            setIsValidated(true);
            setValidationMessage("Adres Verified");
            setAddressConfirmed(true);
        } else {
            setIsValidated(false);
            setValidationMessage("Adres Not Verified");
            setAddressConfirmed(false);
        }
    }, [isOpen, validatedAddressData]);

    const validateAddress = async (formValues, formik) => {
        const { tAddress, vPostCode, vCity, vCountry } = formValues;

        if (!tAddress || !vPostCode || !vCity || !vCountry) {
            setValidationError("Vul alle adresvelden in voordat je valideert.");
            return;
        }

        try {
            setIsValidating(true);
            setModalMessage("");
            setValidationError("");
            setValidationMessage("");
            setFormattedAddress("");
            setAddressConfirmed(false);

            const payload = {
                iAdminId: addressData?.iAdminId,
                tAddress,
                vPostCode,
                vCity,
                vCountry,
            };

            const res = await validateAddressTrigger({
                method: "post",
                data: payload,
            });console.log("validateAddress response:", res);
            if (res?.status === true) {
                setIsValidated(true);
                setValidationMessage("Adres gevalideerd voor automatische afstand berekening");
                setValidationMessageType("");

                if (res?.data?.formattedAddress) {
                    setFormattedAddress(res?.data?.formattedAddress);
                }

                formik.setValues({
                    tAddress: res?.data?.tAddress || formValues.tAddress,
                    vPostCode: res?.data?.vPostcode || formValues.vPostCode,
                    vCity: res?.data?.vCity || formValues.vCity,
                    vCountry: res?.data?.vCountry || formValues.vCountry,
                });
            } else {
                setValidationError("Adres niet gevalideerd");
                setIsValidated(false);
            }
        } catch (error) {
            setValidationError("Validatie mislukt. Controleer het adres opnieuw.");
            setIsValidated(false);
        } finally {
            setIsValidating(false);
        }
    };

    const handleCalculations = async (formValues) => {
        try {
            setModalMessage("");
            setValidationMessage("");
            setIsCalculating(true);

            const payload = {
                iSchoolIds: formDataState?.schoolLists?.join(",") || "",
                iAdminId: addressData?.iAdminId,
                tAddress: formValues.tAddress,
                vCity: formValues.vCity,
                vCountry: formValues.vCountry,
                vPostCode: formValues.vPostCode,
                formattedAddress: formattedAddress
                    ? formattedAddress
                    : addressData?.formattedAddress,
            };

            const response = await calculateDistanceTrigger({
                method: "post",
                data: payload,
            });

            if (response?.status === true) {
                toast.success("Afstanden succesvol berekend!");
                toggle();

                if (apiAftercalculation) {
                    await apiAftercalculation(null, addressData?.iAdminId);
                }
            } else {
                let errorMessage = "";
                if (response?.setting === false) {
                    errorMessage =
                        "De instellingen van Google Maps zijn uitgeschakeld. Neem contact op met uw beheerder.";
                }
                else if (response?.schoolIds === 0) {
                    errorMessage =
                        "Geen geldige schoolbestemmingen gevonden. Controleer de geselecteerde school";
                } else {
                    errorMessage =
                        "Google adres verificatie niet mogelijk, controleer het adres.";
                }

                setModalMessage(errorMessage);
                setModalMessageType("error");
            }
        } catch (error) {
            setModalMessage("Er is een fout opgetreden bij het berekenen van de afstand.");
            setModalMessageType("error");
        } finally {
            setIsCalculating(false);
        }
    };

    const confirmAddress = useCallback(
        async (formikValues, formik) => {
            try {
                const { tAddress, vPostCode, vCity, vCountry } = formikValues;

                if (!tAddress || !vPostCode || !vCity || !vCountry) {
                    setValidationError("Vul alle adresvelden in voordat je bevestigt.");
                    return;
                }

                setValidationError("");
                setApiError("");
                setIsSaving(true);

                const payload = {
                    iAdminId: addressData?.iAdminId,
                    tAddress: formikValues.tAddress,
                    vPostCode: formikValues.vPostCode,
                    vCity: formikValues.vCity,
                    vCountry: formikValues.vCountry,
                    validationInfo: validatedAddressData || {},
                };

                const response = await saveAddressTrigger({
                    method: "post",
                    data: payload,
                });

                if (response?.status === true) {
                    const formatted = response?.data || "";
                    setValidationMessage(" ");
                    setAddressConfirmed(true);
                    setShowCalculateButton(true);
                    formik.setFieldValue("addressDisabled", true);
                    setFormattedAddress(formatted);
                } else {
                    setApiError(response?.message || "Address confirmation failed.");
                }
            } catch (error) {
                console.log(error);
            } finally {
                setIsSaving(false);

                if (apiAfterConfirm) {
                    await apiAfterConfirm();
                }
            }
        },
        [addressData, validatedAddressData]
    );

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            className="feedback-modal-style link-form modal-centered"
        >
            <ModalHeader toggle={toggle}>Adres wijzigen</ModalHeader>

            <Formik
                enableReinitialize
                initialValues={{
                    tAddress: validatedAddressData?.tAddress || addressData?.tAddress || "",
                    vPostCode: validatedAddressData?.vPostCode || addressData?.vPostCode || "",
                    vCity: validatedAddressData?.vCity || addressData?.vCity || "",
                    vCountry: validatedAddressData?.vCountry || addressData?.vCountry || "",
                }}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                    handleCalculations(values);
                }}
            >
                {(formik) => {
                    const { values, handleChange } = formik;

                    const resetValidationState = () => {
                        if (isValidated || validationMessage || validationError) {
                            setIsValidated(false);
                            setValidationMessage("");
                            setValidationError("");
                            setModalMessage("");
                        }
                    };

                    return (
                        <Form>
                            <ModalBody>
                                <FormGroup>
                                    <h4 className="mb-3">
                                        {addressData?.vFirstName + " " + addressData?.vLastName || ""}
                                    </h4>

                                    <Label>Adres:</Label>
                                    <Field
                                        type="text"
                                        name="tAddress"
                                        placeholder="Adres"
                                        className="form-control"
                                        onChange={(e) => {
                                            handleChange(e);
                                            resetValidationState();
                                        }}
                                    />
                                    <ErrorMessage name="tAddress" component="div" className="text-danger small mt-1" />

                                    <Label className="mt-4">Postcode:</Label>
                                    <Field
                                        type="text"
                                        name="vPostCode"
                                        placeholder="Postcode"
                                        className="form-control"
                                        onChange={(e) => {
                                            handleChange(e);
                                            resetValidationState();
                                        }}
                                    />
                                    <ErrorMessage name="vPostCode" component="div" className="text-danger small mt-1" />

                                    <Label className="mt-4">Woonplaats:</Label>
                                    <Field
                                        type="text"
                                        name="vCity"
                                        placeholder="Woonplaats"
                                        className="form-control"
                                        onChange={(e) => {
                                            handleChange(e);
                                            resetValidationState();
                                        }}
                                    />
                                    <ErrorMessage name="vCity" component="div" className="text-danger small mt-1" />

                                    <Label className="mt-4">Land:</Label>
                                    <Field
                                        as="select"
                                        name="vCountry"
                                        className="form-select"
                                        onChange={(e) => {
                                            handleChange(e);
                                            resetValidationState();
                                        }}
                                    >
                                        {cities.map((option) => (
                                            <option key={option.key} value={option.key}>
                                                {option.value}
                                            </option>
                                        ))}
                                    </Field>
                                    <ErrorMessage name="vCountry" component="div" className="text-danger small mt-1" />

                                    {modalMessage && (
                                        <p
                                            className={`text-start small fw-normal mb-1 mt-4 ${modalMessageType === "error" ? "text-danger" : "text-info"
                                                }`}
                                        >
                                            {modalMessage}
                                        </p>
                                    )}

                                    {formattedAddress && isValidated && !addressConfirmed && (
                                        <p className="small mt-3">
                                            Google maps adres: {formattedAddress}
                                        </p>
                                    )}

                                    {validationMessage && (
                                        <p
                                            className={`small mt-3 ${validationMessageType === "error" ? "text-danger" : ""
                                                }`}
                                        >
                                            {validationMessage}
                                        </p>
                                    )}

                                    {validationError && (
                                        <p className="text-danger small mt-3">{validationError}</p>
                                    )}

                                    {apiError && (
                                        <p className="text-danger small mt-3">{apiError}</p>
                                    )}
                                </FormGroup>
                            </ModalBody>

                            <ModalFooter className="d-flex align-items-center justify-content-center py-3">
                                <Button
                                    outline
                                    className="btn-outline-primary-blue btn-color-style py-2 px-3 d-flex gap-2 align-items-center"
                                    type="button"
                                    color="primary"
                                    onClick={() => toggle()}
                                >
                                    Sluiten
                                </Button>

                                {!isValidated ? (
                                    <Button
                                        outline
                                        className="btn-outline-primary-blue btn-color-style py-2 px-3 d-flex gap-2 align-items-center"
                                        type="button"
                                        color="primary"
                                        disabled={isValidating}
                                        onClick={() => validateAddress(values, formik)}
                                    >
                                        <LocationIcon style={{ width: 18, height: 18 }} />
                                        {isValidating ? "Adres wordt gevalideerd..." : "Valideer adres"}
                                    </Button>
                                ) : addressConfirmed ? (
                                    <Button
                                        outline
                                        className="btn-outline-primary-blue btn-color-style py-2 px-3 d-flex gap-2 align-items-center"
                                        type="button"
                                        color="primary"
                                        onClick={() => handleCalculations(values)}
                                        disabled={isCalculating}
                                    >
                                        {isCalculating
                                            ? "Afstanden worden berekend..."
                                            : "Afstanden berekenen"}
                                    </Button>
                                ) : (
                                    <Button
                                        outline
                                        className="btn-outline-primary-blue btn-color-style py-2 px-3 d-flex gap-2 align-items-center"
                                        type="button"
                                        color="primary"
                                        onClick={() => confirmAddress(values, formik)}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? "Adres wordt opgeslagen..." : "Toepassen"}
                                    </Button>
                                )}
                            </ModalFooter>
                        </Form>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default AddressVerificationModal;